import { useState } from "react";
import type { Plan } from "../lib/database.types";
import { useSites } from "../lib/sites";
import { ApiError } from "../lib/api";
import type { Lang } from "../lib/prefs";

const T = {
  es: {
    title: "Nuevo sitio",
    firstTitle: "Crea tu primer sitio",
    sub: "Añade el dominio que quieres monitorear y una breve descripción del negocio.",
    domain: "Dominio",
    domainPh: "ejemplo.com.mx",
    desc: "Descripción del negocio",
    descPh: "Ej. Lavandería a domicilio en Guadalajara, entrega el mismo día.",
    plan: "Plan",
    freeUsed:
      "Ya tienes un sitio Gratis en tu cuenta. Los sitios adicionales requieren un plan de pago.",
    planFree: "Gratis",
    planFreeD: "1 perfil · reporte mensual",
    planPro: "Pro",
    planProD: "$799 MXN/mes · prueba 30 días · semanal",
    planEnt: "Enterprise",
    planEntD: "$2,999 MXN/mes · semanal",
    cancel: "Cancelar",
    create: "Crear sitio",
    creating: "Creando…",
    errDomain: "Escribe un dominio válido (ej. ejemplo.com).",
    errFree:
      "Ya tienes un sitio Gratis; elige un plan de pago para este sitio.",
    errGeneric: "No se pudo crear el sitio. Intenta de nuevo.",
  },
  en: {
    title: "New site",
    firstTitle: "Create your first site",
    sub: "Add the domain you want to track and a short business description.",
    domain: "Domain",
    domainPh: "example.com",
    desc: "Business description",
    descPh: "e.g. On-demand laundry in Guadalajara with same-day delivery.",
    plan: "Plan",
    freeUsed:
      "Your account already has a Free site. Additional sites need a paid plan.",
    planFree: "Free",
    planFreeD: "1 persona · monthly report",
    planPro: "Pro",
    planProD: "$799 MXN/mo · 30-day trial · weekly",
    planEnt: "Enterprise",
    planEntD: "$2,999 MXN/mo · weekly",
    cancel: "Cancel",
    create: "Create site",
    creating: "Creating…",
    errDomain: "Enter a valid domain (e.g. example.com).",
    errFree: "You already have a Free site; pick a paid plan for this one.",
    errGeneric: "Could not create the site. Please try again.",
  },
};

// Create-site flow. When the account already has a free site, Free is disabled
// and the picker defaults to Pro (server enforces this too). `mandatory` renders
// it as the zero-sites onboarding step (no close button, no scrim dismissal).
export default function CreateSiteModal({
  lang,
  onClose,
  mandatory = false,
}: {
  lang: Lang;
  onClose?: () => void;
  mandatory?: boolean;
}) {
  const t = T[lang];
  const { createSite, hasFreeSite } = useSites();
  const [domain, setDomain] = useState("");
  const [description, setDescription] = useState("");
  const [plan, setPlan] = useState<Plan>(hasFreeSite ? "pro" : "free");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!domain.trim()) {
      setErr(t.errDomain);
      return;
    }
    setBusy(true);
    try {
      await createSite({ domain, description, plan });
      onClose?.();
    } catch (e2) {
      if (e2 instanceof ApiError && e2.code === "invalid_domain") setErr(t.errDomain);
      else if (e2 instanceof ApiError && e2.code === "free_site_exists") setErr(t.errFree);
      else setErr(t.errGeneric);
      setBusy(false);
    }
  }

  const planOpts: { id: Plan; nm: string; d: string; disabled: boolean }[] = [
    { id: "free", nm: t.planFree, d: t.planFreeD, disabled: hasFreeSite },
    { id: "pro", nm: t.planPro, d: t.planProD, disabled: false },
    { id: "enterprise", nm: t.planEnt, d: t.planEntD, disabled: false },
  ];

  return (
    <>
      <div
        className="scrim on"
        onClick={mandatory ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className="modal on"
        role="dialog"
        aria-modal="true"
        aria-label={mandatory ? t.firstTitle : t.title}
      >
        <div className="modal-h">
          <h3>{mandatory ? t.firstTitle : t.title}</h3>
          {!mandatory && (
            <button className="modal-x" onClick={onClose} aria-label={t.cancel}>
              ✕
            </button>
          )}
        </div>
        <form className="modal-b" onSubmit={submit}>
          <p className="sub-line" style={{ marginTop: 0 }}>
            {t.sub}
          </p>
          {err && <p className="auth-err">{err}</p>}

          <div className="fld">
            <label htmlFor="cs-domain">{t.domain}</label>
            <input
              id="cs-domain"
              className="inp"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder={t.domainPh}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="fld">
            <label htmlFor="cs-desc">{t.desc}</label>
            <textarea
              id="cs-desc"
              className="inp"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.descPh}
            />
          </div>

          <div className="fld">
            <label>{t.plan}</label>
            {hasFreeSite && <p className="hint">{t.freeUsed}</p>}
            <div className="planpick">
              {planOpts.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  className={`planopt${plan === o.id ? " on" : ""}`}
                  disabled={o.disabled}
                  onClick={() => setPlan(o.id)}
                >
                  <span className="nm">{o.nm}</span>
                  <span className="d">{o.d}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="modal-foot">
            {!mandatory && (
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                {t.cancel}
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t.creating : t.create}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
